import { gql } from 'apollo-angular';

/**
 * Partner list rows: exactly what the table renders plus what the Download
 * report reads (document counts, and the first vehicle's service flags).
 *
 * Deliberately narrow. It used to pull every partner-document field including
 * `notes` and the full resubmission `history`, none of which the list shows —
 * and one document row with a NULL `notes` was enough to fail the whole query,
 * because GraphQL propagates a non-nullable null up to the root. The details
 * page has its own query (PARTNER_DETAIL) for the full record.
 *
 * Everything in here exists on every deployed API.
 *
 * The vehicle/service filters are NOT part of this document: declaring
 * `$vehicleClass: PartnerVehicleClassFilter` fails validation outright on an
 * API that predates them, which would take the whole Partners page down rather
 * than just the filters. The component uses PARTNERS_LIST_FILTERED only when a
 * filter is actually selected — see partnersListQuery().
 */
const PARTNER_LIST_SELECTION = `
            partnerId
            membershipId
            firstName
            middleName
            lastName
            vehicleType
            status
            dateCreated
            dateApprove
            partnerDocuments{
                partnerDocumentId
                isDone
            }
            partnerVehicles{
                vehicleId
                isExpress
                isOkRide
                isMototaxi
                isTaxi
                vehicle {
                    category
                    seatCount
                }
            }`;

const partnersListDocument = (filterParams: string, filterArgs: string) => gql`
    query partners(
        $page: Int
        $status: PartnerStatusFilter
        $querySearch: String
        $start: String
        $end: String
        $ids: [PartnerHashedId!]
        $sortBy: PartnerSortingInput${filterParams}
    ) {
        partners(
            page: $page
            status: $status
            querySearch: $querySearch
            start: $start
            end: $end
            ids: $ids
            sortBy: $sortBy${filterArgs}
        ) {
            ${PARTNER_LIST_SELECTION}
        }
    }
`;

export const PARTNERS_LIST = partnersListDocument('', '');

/** Adds the vehicle/service filters; needs an API that has them. */
export const PARTNERS_LIST_FILTERED = partnersListDocument(
    `
        $vehicleClass: PartnerVehicleClassFilter
        $service: PartnerServiceFilter`,
    `
            vehicleClass: $vehicleClass
            service: $service`,
);

/** The narrowest document that can answer the filters in play. */
export function partnersListQuery(filters: {
    vehicleClass?: string | null;
    service?: string | null;
}) {
    return filters.vehicleClass || filters.service
        ? PARTNERS_LIST_FILTERED
        : PARTNERS_LIST;
}


export const PARTNERS_OVERVIEW = gql`
    query partnerOverview {
        partnerOverview {
            forReviewCount
            forAmmendmentCount
            approvedCount
            initialReviewCount
            rideHailingFourWheelsCount
            rideHailingTwoWheelsCount
            rideExpressFourWheelsCount
            rideExpressTwoWheelsCount
            totalApprovedTwowheels
            totalApprovedFourWheels
        }
    }
`;

/**
 * Partners > Summary, and the counts beside the status tabs on Partner List —
 * they are the same five numbers, so one query serves both.
 *
 * `asOf` is an inclusive cut-off on registration date; everyone still counts by
 * the status they hold today. Labels are built in the component: the API sends
 * keys so the wording can change without a schema change.
 */
export const PARTNER_SUMMARY = gql`
    query partnerSummary($asOf: String) {
        partnerSummary(asOf: $asOf) {
            asOf
            pipeline {
                totalRegistrations
                underReview
                withDocuments
                forAmendment
                approved
            }
            byService {
                key
                vehicleClass
                count
                shareOfApproved
            }
            byVehicle {
                vehicleClass
                total
                rows {
                    key
                    count
                }
            }
        }
    }
`;

/**
 * Activity History tab: a partner's audited changes, newest first, with the
 * admin who made each one. Paginated rather than read off `partners.auditTrails`
 * so a long history is not fetched whole to show ten rows.
 */
export const PARTNER_ACTIVITY_HISTORY = gql`
    query partnerActivityHistory($partnerId: PartnerHashedId!, $page: Int, $limit: Int) {
        partnerActivityHistory(partnerId: $partnerId, page: $page, limit: $limit) {
            total
            page
            limit
            totalPages
            items {
                partnerAuditTrailId
                partnerDocumentId
                action
                adminId
                adminName
                adminEmail
                oldValues
                newValues
                dateCreated
            }
        }
    }
`;

export const PARTNER_BOOKING_EXPRESS_TXNS = gql`
    query bookingExpressTxns($partnerId:PartnerHashedId, $sortBy:BookingExpressSortingInput) {
        bookingExpressTxns(partnerId:$partnerId, sortBy:$sortBy) {
            bookingExpressId
            status
            notes
            dateCreated
            startFormattedAddress
            endFormattedAddress
            partnerRating {
                rate
            }
            user {
                dateCreated
                email
                isVerify
                membershipId
                mobile
                name
                currentPoints
                usedPoints
                userId
            }
            partner {
                partnerId
                tier
                membershipId
                firstName
                middleName
                lastName
                mobile
                email
                distributionArea
                vehicleType
                licensePlate
                isOtpVerify
                isApprove
                dateCreated
                currentPoints
                usedPoints
            }
            totalPrice
            orderRoutes
            totalDistance
            discountPrice
            farePrice
        }
    }
`;

export const PARTNER_BOOKING_RIDE_TXNS = gql`
    query bookingRideTxns($partnerId:PartnerHashedId, $service:BookingRideServiceFilterInput!, $sortBy:BookingRideSortingInput) {
        bookingRideTxns(partnerId:$partnerId, service:$service, sortBy:$sortBy) {
            bookingRideId
            status
            dateCreated
            startFormattedAddress
            endFormattedAddress
            rating {
                rate
            }
            user {
                dateCreated
                email
                isVerify
                membershipId
                mobile
                name
                currentPoints
                usedPoints
                userId
            }
            partner {
                partnerId
                tier
                membershipId
                firstName
                middleName
                lastName
                mobile
                email
                distributionArea
                vehicleType
                licensePlate
                isOtpVerify
                isApprove
                dateCreated
                currentPoints
                usedPoints
            }
            totalPrice
            routes
            kmDistance
            discountPrice
            farePrice
        }
    }
`;

export const PARTNER_WITHDRAWAL_REQUESTS = gql`
    query withdrawalRequests(
        $page: Int,
        $status: WithdrawalRequestStatusEnum!
        $withdrawalWalletFilterInput: WithdrawalWalletFilterInput
        $dateFilterInput: WithdrawalWalletDateFilterInput!
    ) {
        withdrawalRequests(
            page:$page,
            status: $status
            withdrawalWalletFilterInput: $withdrawalWalletFilterInput
            dateFilterInput: $dateFilterInput
        ) {
            partnerWalletRequestId
            bankCode
            bankName
            accountNumber
            accountName
            amount
            remarks
            status
            dateApproved
            dateCancelled
            dateUpdated
            dateCreated
            partner {
                partnerId
                firstName
                lastName
                email
                vehicleType
            }
        }
    }
`;

export const PARTNER_TOPUP_REQUESTS = gql`
    query topupRequestList(
        $page: Int
        $status: PartnerTopupStatusEnum!
        $source: PartnerTopupSourceEnum!
        $partnerFilter: TopupPartnerFilterInput
        $dateFilter: TopupDateFilterInput
        $topUpRequestId: TopupHashedId
    ) {
        topupRequestList(
            page: $page
            status: $status
            source: $source
            partnerFilter: $partnerFilter
            dateFilter: $dateFilter
            topUpRequestId: $topUpRequestId
        ) {
            partnerTopupRequestId
            source
            amount
            status
            paymentId
            dateCreated
            dateUpdated
            partner {
                partnerId
                firstName
                lastName
                mobile
            }
        }
    }
`;

export const PARTNER_DETAIL = gql`
  query partnerDetail($ids: [PartnerHashedId!]) {
    partners(ids: $ids) {
      partnerId
      tier
      membershipId
      firstName
      middleName
      lastName
      mobile
      email
      distributionArea
      vehicleType
      licensePlate
      isOtpVerify
      isApprove
      latitude
      longitude
      dateCreated
      rating
      currentPoints
      usedPoints
      status
      photo
      partnerDocuments {
        partnerDocumentId
        type
        subType
        link
        isDone
        status
        idNumber
        firstName
        middleName
        lastName
        address
        expirationDate
        gender
        birthdate
        dateApprove
        dateCreated
        notes
        history {
          partnerDocumentHistoryId
          type
          subType
          link
          dateCreated
        }
      }
      partnerVehicles {
        vehicleId
        dateCreated
        status
        licensePlate
        vehicleModel
        yearModel
        isExpress
        isOkRide
        isMototaxi
        isTaxi
        vehicle {
          category
          description
        }
      }
      fleetRegistry {
        fleetVehicleRegistryId
        fleetId
        fleetAccountId
        plateNumber
        conductionNumber
        vehicleType
        vehicleModel
        yearModel
        vehicleMaker
        vehicleId
        status
        dateCreated
        dateUpdated
        fleet {
          operatorName
          registeredBusinessName
          representativeFirstName
          representativeLastName
          representativeMobile
          ltfrbCpcNumber
          fleetType
          cpcExpirationDate
          status
          declineRemarks
          dateApproved
          dateCreated
          dateUpdated
        }
      }
      auditTrails {
        partnerAuditTrailId
        partnerDocumentId
        adminId
        action
        oldValues
        newValues
        dateCreated
      }
    }
  }
`;
